import {
  HERO_HEIGHT,
  HERO_IMAGE,
  HERO_SIZES,
  HERO_SRCSET,
  HERO_WIDTH,
} from "@/lib/hero-image";

export function Hero() {
  return (
    <section id="top" className="relative isolate overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
      {/* Decorative blobs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 top-24 hidden h-[420px] w-[420px] rounded-full bg-sage/30 blur-3xl animate-float md:block"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-72 hidden h-[360px] w-[360px] rounded-full bg-clay/20 blur-3xl animate-float md:block"
        style={{ animationDelay: "-6s" }}
      />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-sage/40 bg-sage-soft/60 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-sage-deep">
            <span className="h-1.5 w-1.5 rounded-full bg-sage-deep" />
            Now accepting new clients
          </span>
          <h1 className="mt-6 text-5xl font-medium leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
            A quieter place to{" "}
            <span className="relative inline-block">
              <span className="relative z-10 italic">think things</span>
              <svg
                aria-hidden
                viewBox="0 0 300 14"
                className="absolute -bottom-2 left-0 z-0 w-full text-sage"
                preserveAspectRatio="none"
              >
                <path
                  d="M2 9 C 80 -2, 220 -2, 298 9"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>{" "}
            through.
          </h1>
          <p className="hero-lede mt-6 max-w-xl text-lg leading-relaxed text-foreground/75">
            Compassionate counseling with Jerome Bottelberghe, LPC. Telehealth and in-person
            sessions for individuals, couples, and families navigating divorce, custody, abuse
            recovery, LGBTQ+ life, and the everyday weight of being human.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-base font-medium text-primary-foreground shadow-[var(--shadow-lift)] transition-transform hover:-translate-y-0.5"
            >
              Schedule a free intro
              <span aria-hidden>→</span>
            </a>
            <a
              href="tel:5075302029"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3.5 text-base font-medium text-foreground transition-colors hover:bg-secondary"
            >
              <PhoneIcon />
              (507) 530‑2029
            </a>
          </div>
          <dl className="mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-border/70 pt-8">
            {[
              ["20+", "years of practice"],
              ["LPC", "licensed in MN"],
              ["Free", "intro session"],
            ].map(([n, l]) => (
              <div key={l}>
                <dt className="font-display text-3xl text-sage-deep">{n}</dt>
                <dd className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">
                  {l}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="relative">
          <div className="relative grain overflow-hidden rounded-3xl shadow-[var(--shadow-lift)]">
            <img
              src={HERO_IMAGE}
              srcSet={HERO_SRCSET}
              alt="Sunlit therapy office with a sage-green armchair, sheer linen curtains, and a small wooden side table holding an open notebook"
              width={HERO_WIDTH}
              height={HERO_HEIGHT}
              sizes={HERO_SIZES}
              className="aspect-[5/6] w-full object-cover"
              fetchPriority="high"
              loading="eager"
              decoding="async"
            />
          </div>
          {/* Floating quote card */}
          <div className="absolute -bottom-8 -left-6 max-w-[260px] rounded-2xl bg-card p-5 shadow-[var(--shadow-lift)] sm:-left-10">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-sage-deep">
              <LeafIcon /> First conversation is free
            </div>
            <p className="mt-2 font-display text-base leading-snug">
              "I listen the first time. That's where the work begins."
            </p>
            <p className="mt-2 text-xs text-muted-foreground">— Jerry</p>
          </div>
          {/* Floating badge */}
          <div className="absolute -right-4 top-8 hidden rotate-3 rounded-full border border-border bg-card px-4 py-2 text-xs font-medium shadow-[var(--shadow-soft)] sm:block">
            🌿 Telehealth & in-person
          </div>
        </div>
      </div>
    </section>
  );
}

function PhoneIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}
function LeafIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M11 20A7 7 0 0 1 4 13c0-6 7-11 16-11 0 9-5 16-11 16Z"/><path d="M2 22c4-3 6-7 7-12"/>
    </svg>
  );
}
